import { useTestCaseContext } from '@Context/TestCaseContext';
import { TestCaseProp, TestCases } from '@Models/types';
import { Trash } from '@phosphor-icons/react';
import { Dispatch, SetStateAction } from 'react';

interface Props {
  viewedTestcase?: TestCaseProp | null;
  selected: TestCases[];
  setSelected: Dispatch<SetStateAction<TestCases[]>>;
  setSelectedTestCase: (
    testcase: TestCaseProp,
    index: number,
    refNum?: string
  ) => void;
  filteredArray: TestCases[];
}

const TestCaseList: React.FC<Props> = ({
  viewedTestcase,
  selected,
  setSelected,
  setSelectedTestCase,
  filteredArray,
}) => {
  const { selectedGenerated } = useTestCaseContext();

  if (!selectedGenerated) {
    return <div></div>;
  }

  const clearSelection = () => {
    setSelected([]);
  };

  const getSelectedCount = () => {
    let ctr = 0;
    selected.forEach((s) => {
      s.GeneratedTestCase.forEach(() => {
        ctr++;
      });
    });
    return ctr;
  };

  const handleSelect = (item: TestCases, checked: boolean) => {
    setSelected((prevSelected) => {
      if (checked) {
        if (
          !prevSelected.find((selectedItem) => selectedItem.name === item.name)
        ) {
          return [
            ...prevSelected,
            {
              name: item.name,
              GeneratedTestCase: item.GeneratedTestCase,
              reference_num: item.reference_num,
            },
          ];
        }
      } else {
        return prevSelected.filter(
          (selectedItem) => selectedItem.name !== item.name
        );
      }
      return prevSelected;
    });
  };

  const handleInnerSelect = (
    parent: TestCases,
    testCase: TestCaseProp,
    checked: boolean
  ) => {
    setSelected((prevSelected) => {
      const parentIndex = prevSelected.findIndex(
        (selectedItem) => selectedItem.name === parent.name
      );

      if (checked) {
        if (parentIndex === -1) {
          return [
            ...prevSelected,
            {
              name: parent.name,
              GeneratedTestCase: [testCase],
              reference_num: parent.reference_num,
            },
          ];
        } else {
          const updatedParent = { ...prevSelected[parentIndex] };
          updatedParent.GeneratedTestCase.push(testCase);
          const updatedSelected = [...prevSelected];
          updatedSelected[parentIndex] = updatedParent;
          return updatedSelected;
        }
      } else {
        if (parentIndex !== -1) {
          const updatedParent = { ...prevSelected[parentIndex] };
          updatedParent.GeneratedTestCase =
            updatedParent.GeneratedTestCase.filter(
              (tc) => tc.title !== testCase.title
            );
          if (updatedParent.GeneratedTestCase.length === 0) {
            return prevSelected.filter(
              (selectedItem) => selectedItem.name !== parent.name
            );
          } else {
            const updatedSelected = [...prevSelected];
            updatedSelected[parentIndex] = updatedParent;
            return updatedSelected;
          }
        }
      }
      return prevSelected;
    });
  };

  return (
    <>
      <div className="mt-2 flex flex-col rounded-md border p-2 shadow-lg">
        <div
          className={`text-md flex flex-row items-center justify-between font-semibold ${selected.length === 0 ? 'text-gray-500' : 'text-primary-700'}`}
        >
          <label>Selected</label>
          <span className="flex flex-row items-center gap-x-1">
            <label data-testid="test-test-case-selected-count">
              {getSelectedCount()}
            </label>
            <Trash
              data-testid="test-test-case-clear-btn"
              weight="bold"
              className="cursor-pointer hover:text-brandRed-500"
              onClick={clearSelection}
            />
          </span>
        </div>
      </div>
      <div className="custom-scroll flex h-0 flex-grow flex-col overflow-y-auto">
        {filteredArray.map((item, index) => (
          <div key={index} className="flex flex-col">
            <div className="my-4 border"></div>
            <span className="flex flex-col items-start">
              <label className="rounded-md bg-primary-500 px-2 py-1 text-xs text-white">
                {item.reference_num}
              </label>
              <div className="flex flex-row items-center gap-x-2">
                <input
                  data-testid={`test-test-case-checkbox-${item.name}`}
                  type="checkbox"
                  checked={selected.some(
                    (selectedItem) => selectedItem.name === item.name
                  )}
                  onChange={(e) => handleSelect(item, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  data-testid={`test-test-case-label-${item.name}`}
                  className="text h-auto p-2 text-sm font-medium text-primary-700 sm:text-base md:text-lg"
                >
                  {item.name}``
                </label>
              </div>
            </span>
            <div className="flex flex-col gap-y-1">
              {item.GeneratedTestCase.map((tc, index) => (
                <span
                  key={index}
                  onClick={() =>
                    setSelectedTestCase(tc, index, item.reference_num)
                  }
                  className={`flex cursor-pointer flex-row items-center gap-x-2 px-2 py-0.5 text-xs hover:bg-primary-100 ${viewedTestcase?.title === tc.title ? 'bg-primary-200' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={
                      selected
                        .find((selectedItem) => selectedItem.name === item.name)
                        ?.GeneratedTestCase.some((t) => t.title === tc.title) ||
                      false
                    }
                    onChange={(e) =>
                      handleInnerSelect(item, tc, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label className="px-2">{tc.title}</label>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default TestCaseList;
